import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PiGlobeLight } from 'react-icons/pi';
import {
  ApiError,
  WebsiteCatalogSort,
  WebsiteDeploymentStatus,
  WebsiteDomainStatus,
  WebsiteDomainType,
  type TCCompany,
  type WebsiteSection,
  WebsiteStatus,
  type WebsiteTemplateRegistryTemplateSummary,
  type WebsiteReadResponse,
  type WebsiteUpsertRequest,
  type WebsiteWhatsAppCtaSlot,
} from 'src/api/generated';
import Loading from 'src/modules/shared/components/atoms/loading';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { useAlert } from 'src/contexts/alert-context';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  AdminTenantPermission,
  WebsitePermission,
} from 'src/modules/shared/types/permissions.type';
import type { TranslateParams } from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import { classes } from 'src/modules/shared/utils/styles.util';
import { WebsiteMobileActionBar } from '../molecules/website-mobile-action-bar';
import { WebsitePreviewSheet } from '../molecules/website-preview-sheet';
import { WebsiteSettingsHeader } from '../molecules/website-settings-header';
import { WebsiteSettingsSteps } from '../molecules/website-settings-steps';
import { WebsiteTemplateMockPreview } from '../molecules/website-template-mock-preview';
import { WebsiteAddressStep } from '../organisms/website-address-step';
import { WebsiteBrandStep } from '../organisms/website-brand-step';
import { WebsiteContactStep } from '../organisms/website-contact-step';
import { WebsiteContentStep } from '../organisms/website-content-step';
import { WebsiteDesignStep } from '../organisms/website-design-step';
import {
  useCreateWebsiteDomain,
  useCreateWebsiteLogoUploadSession,
  useCreateWebsitePreviewSession,
  useCurrentCompany,
  useCurrentWebsite,
  useFinalizeWebsiteLogoUpload,
  usePublishWebsite,
  useUpsertWebsite,
  useVerifyWebsiteDomain,
  useWebsiteAnalytics,
  useWebsiteTemplates,
} from '../../hooks/api/websites';
import {
  calculateWebsiteLogoChecksum,
  uploadWebsiteLogoBytes,
  validateWebsiteLogoFile,
  WebsiteLogoUploadError,
  WebsiteLogoValidationError,
} from '../../utils/website-logo-upload.util';
import type { WebsiteLogoValidationErrorCode } from '../../types/website-logo-upload.types';
import {
  buildDefaultProvisionalSlug,
  normalizeProvisionalSlug,
} from '../../utils/website-slug.util';
import {
  getWebsiteSectionInputText,
  getWebsiteServicesInputText,
} from '../../utils/website-section-input.util';
import { resolveSelectedWebsiteTemplate } from '../../utils/template-preview.util';
import {
  isWebsiteContactHttpUrlValid,
  isWebsiteContactInformationValid,
} from '../../utils/website-contact-validation.util';
import {
  hasUnpublishedWebsiteChanges,
  isWebsiteDeploymentInProgress,
} from '../../utils/website-publish-state.util';
import type { WebsiteSettingsStep } from '../../utils/website-settings-steps.util';
import {
  DEFAULT_WEBSITE_TEMPLATE_KEY,
  filterWebsiteTemplatesForPicker,
  sortWebsiteTemplatesForPicker,
} from '../../utils/website-template-catalog.util';
import { resolveWebsitePreviewHost } from '../../utils/website-site-url.util';
import { WEBSITE_WHATSAPP_CTA_CONFIGS } from '../../utils/website-whatsapp-cta.util';

const WEBSITE_DEPLOYMENT_STATUS = WebsiteDeploymentStatus;
const WEBSITE_DOMAIN_STATUS = WebsiteDomainStatus;
const WEBSITE_DOMAIN_TYPE = WebsiteDomainType;
const WEBSITE_STATUS = WebsiteStatus;
type WebsiteSettingsTranslate = (
  key: TranslationKey,
  params?: TranslateParams
) => string;

const LOGO_VALIDATION_TRANSLATION_KEYS: Record<
  WebsiteLogoValidationErrorCode,
  TranslationKey
> = {
  empty: 'websites.settings.logo.errors.empty',
  'too-large': 'websites.settings.logo.errors.tooLarge',
  'unsupported-type': 'websites.settings.logo.errors.unsupportedType',
  'extension-mismatch': 'websites.settings.logo.errors.extensionMismatch',
};

const canStartWebsiteLogoUpload = (
  canUpdateWebsite: boolean,
  logoSupported: boolean,
  logoBusy: boolean
): boolean => canUpdateWebsite && logoSupported && !logoBusy;

const isCurrentWebsiteLogoUploadOperation = (
  currentOperationId: number,
  expectedOperationId: number,
  signal: AbortSignal
): boolean => currentOperationId === expectedOperationId && !signal.aborted;

const shouldIgnoreWebsiteLogoUploadError = (
  error: unknown,
  isCurrentOperation: boolean
): boolean =>
  !isCurrentOperation ||
  (error instanceof WebsiteLogoUploadError && error.code === 'aborted');

const isWebsiteTemplateSelectionDisabled = (
  canUpdateWebsite: boolean,
  logoBusy: boolean,
  pendingLogoFileId: string | null
): boolean => !canUpdateWebsite || logoBusy || Boolean(pendingLogoFileId);

const PROVISIONAL_SLUG_UNAVAILABLE_API_MESSAGE =
  'Website provisional slug is already in use.';
const WEBSITE_LOGO_NOT_DURABLE_API_MESSAGE =
  'Managed website logo is not durable or does not match its saved URL.';
const WEBSITE_LOGO_PUBLISH_RETRY_DELAYS_MS = [
  0, 250, 500, 1_000, 2_000, 4_000, 8_000,
] as const;

const getApiErrorBodyError = (error: unknown): string | undefined => {
  if (
    !(error instanceof ApiError) ||
    error.body === null ||
    typeof error.body !== 'object'
  ) {
    return undefined;
  }

  const body = error.body as { error?: unknown };
  return typeof body.error === 'string' ? body.error : undefined;
};

const isProvisionalSlugUnavailableError = (error: unknown): boolean =>
  getApiErrorBodyError(error) === PROVISIONAL_SLUG_UNAVAILABLE_API_MESSAGE ||
  extractApiErrorMessage(error) === PROVISIONAL_SLUG_UNAVAILABLE_API_MESSAGE;

const isWebsiteLogoDurabilityPendingError = (error: unknown): boolean =>
  getApiErrorBodyError(error) === WEBSITE_LOGO_NOT_DURABLE_API_MESSAGE;

const waitForWebsiteLogoPublishRetry = (delayMs: number): Promise<void> =>
  delayMs === 0
    ? Promise.resolve()
    : new Promise((resolve) => window.setTimeout(resolve, delayMs));

/**
 * Retries the publish preflight while the asynchronously dispatched logo commit is pending.
 *
 * @param publish - Calls the current Website publish endpoint.
 * @param onWaiting - Announces that the saved logo is still becoming durable.
 * @returns The Website response from the first successful publish attempt.
 * @throws The last durability error after the bounded retry budget, or any non-durability error.
 */
const publishWhenWebsiteLogoIsDurable = async (
  publish: () => Promise<WebsiteReadResponse>,
  onWaiting: () => void
): Promise<WebsiteReadResponse> => {
  for (const retryDelay of WEBSITE_LOGO_PUBLISH_RETRY_DELAYS_MS) {
    try {
      return await publish();
    } catch (error) {
      if (!isWebsiteLogoDurabilityPendingError(error)) {
        throw error;
      }

      onWaiting();
      await waitForWebsiteLogoPublishRetry(retryDelay);
    }
  }

  return publish();
};

const isWebsiteSettingsFormValid = (form: WebsiteUpsertRequest): boolean =>
  Boolean(form.templateKey) &&
  form.templateVersion > 0 &&
  Boolean(form.provisionalSlug) &&
  Boolean(form.draftConfig.content.siteName) &&
  Boolean(form.draftConfig.whatsApp.phoneNumber) &&
  isWebsiteContactInformationValid(form.draftConfig.content) &&
  isWebsiteContactHttpUrlValid(form.draftConfig.theme?.heroImageUrl);

type ThemeColorKey = 'primaryColor' | 'secondaryColor' | 'accentColor';
type BrochureSectionType =
  | 'neighborhoods'
  | 'services'
  | 'faqs'
  | 'legal'
  | 'external-links';

const BROCHURE_SECTION_ORDER: BrochureSectionType[] = [
  'neighborhoods',
  'services',
  'faqs',
  'legal',
  'external-links',
];

const parseLineList = (value: string): string[] =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const parseFaqItems = (value: string): { question: string; answer: string }[] =>
  parseLineList(value).flatMap((line) => {
    const [question, ...answerParts] = line.split('|');
    const answer = answerParts.join('|').trim();
    const normalizedQuestion = question.trim();

    return normalizedQuestion && answer
      ? [{ question: normalizedQuestion, answer }]
      : [];
  });

const parseExternalLinks = (value: string): { label: string; url: string }[] =>
  parseLineList(value).flatMap((line) => {
    const [label, ...urlParts] = line.split('|');
    const url = urlParts.join('|').trim();
    const normalizedLabel = label.trim();

    return normalizedLabel && url ? [{ label: normalizedLabel, url }] : [];
  });

const formatStringItems = (content: Record<string, unknown>): string => {
  const items = content.items;
  return Array.isArray(items)
    ? items
        .filter((item): item is string => typeof item === 'string')
        .join('\n')
    : '';
};

const formatFaqItems = (content: Record<string, unknown>): string => {
  const items = content.items;
  if (!Array.isArray(items)) return '';

  return items
    .flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const record = item as Record<string, unknown>;
      const question =
        typeof record.question === 'string' ? record.question : '';
      const answer = typeof record.answer === 'string' ? record.answer : '';
      return question && answer ? [`${question} | ${answer}`] : [];
    })
    .join('\n');
};

const formatExternalLinks = (content: Record<string, unknown>): string => {
  const items = content.items;
  if (!Array.isArray(items)) return '';

  return items
    .flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const record = item as Record<string, unknown>;
      const label = typeof record.label === 'string' ? record.label : '';
      const url = typeof record.url === 'string' ? record.url : '';
      return label && url ? [`${label} | ${url}`] : [];
    })
    .join('\n');
};

const formatLegalText = (content: Record<string, unknown>): string =>
  typeof content.text === 'string' ? content.text : '';

const upsertBrochureSection = (
  sections: WebsiteSection[] | undefined,
  section: WebsiteSection,
  hasValue: boolean
): WebsiteSection[] => {
  const existingSections = sections ?? [];
  const otherSections = existingSections.filter(
    (item) => item.type !== section.type
  );
  const nextSections = hasValue ? [...otherSections, section] : otherSections;

  return nextSections.sort((left, right) => {
    const leftIndex = BROCHURE_SECTION_ORDER.indexOf(
      left.type as BrochureSectionType
    );
    const rightIndex = BROCHURE_SECTION_ORDER.indexOf(
      right.type as BrochureSectionType
    );

    return (
      (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex) -
      (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex)
    );
  });
};

const buildInitialForm = (
  t: WebsiteSettingsTranslate,
  company?: Pick<TCCompany, 'id' | 'name'>,
  selectedTemplate?: WebsiteTemplateRegistryTemplateSummary
): WebsiteUpsertRequest => {
  const siteName = company?.name ?? 'Mi sitio inmobiliario';

  return {
    templateKey: selectedTemplate?.key ?? DEFAULT_WEBSITE_TEMPLATE_KEY,
    templateVersion: selectedTemplate?.version ?? 1,
    registryTemplateId: selectedTemplate?.registryTemplateId,
    provisionalSlug: company
      ? buildDefaultProvisionalSlug(siteName, company.id)
      : '',
    draftConfig: {
      content: {
        siteName,
        headline: '',
        subheadline: '',
        description: '',
        address: '',
        email: '',
        businessHours: '',
        phone: '',
        instagramUrl: '',
        facebookUrl: '',
        tiktokUrl: '',
        services: [],
        navigation: [
          { label: t('websites.settings.navigation.home'), href: '#home' },
          {
            label: t('websites.settings.navigation.properties'),
            href: '#properties',
          },
          {
            label: t('websites.settings.navigation.contact'),
            href: '#contact',
          },
        ],
        sections: [],
      },
      theme: {
        primaryColor: '#0f172a',
        secondaryColor: '#1e293b',
        accentColor: '#0ea5e9',
      },
      seo: {
        title: siteName,
        description: '',
      },
      whatsApp: {
        phoneNumber: '',
        templates: WEBSITE_WHATSAPP_CTA_CONFIGS.map((item) => ({
          slot: item.slot,
          label: t(item.labelKey),
          messageTemplate: t(item.defaultMessageKey),
        })),
      },
      catalog: {
        defaultSort: WebsiteCatalogSort.UPDATED_DESC,
      },
      featureFlags: {
        portalParityFilters: true,
      },
    },
  };
};

export const WebsitesSettingsPage: React.FC = () => {
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const {
    hasPermission: canReadWebsite,
    loading: loadingReadWebsitePermission,
  } = useHasPermissions(WebsitePermission.Read);
  const {
    hasPermission: canUpdateWebsite,
    loading: loadingUpdateWebsitePermission,
  } = useHasPermissions(WebsitePermission.Update);
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
  } = useWebsiteTemplates();
  const {
    website,
    loading: websiteLoading,
    error: websiteError,
    refetch: refetchWebsite,
  } = useCurrentWebsite();
  const {
    company,
    loading: companyLoading,
    error: companyError,
  } = useCurrentCompany();
  const {
    analytics,
    loading: analyticsLoading,
    error: analyticsError,
  } = useWebsiteAnalytics(30);
  const [customDomain, setCustomDomain] = useState('');
  const [trackedDeploymentId, setTrackedDeploymentId] = useState<number | null>(
    null
  );
  const [provisionalSlugError, setProvisionalSlugError] = useState<
    string | null
  >(null);
  const [form, setForm] = useState<WebsiteUpsertRequest>(() =>
    buildInitialForm(t)
  );
  const [pendingLogoFileId, setPendingLogoFileId] = useState<string | null>(
    null
  );
  const pendingLogoFileIdRef = useRef<string | null>(null);
  const logoUploadAbortControllerRef = useRef<AbortController | null>(null);
  const logoUploadOperationRef = useRef(0);
  const logoUploadActiveRef = useRef(false);
  const [logoDraftDirty, setLogoDraftDirty] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoUploadActive, setLogoUploadActive] = useState(false);
  const [logoPublishRetryActive, setLogoPublishRetryActive] = useState(false);
  const [logoStatusMessage, setLogoStatusMessage] = useState<string>();
  const [logoErrorMessage, setLogoErrorMessage] = useState<string>();
  const syncedFormSourceRef = useRef<string | null>(null);
  const [activeStep, setActiveStep] = useState<WebsiteSettingsStep>('design');
  const [previewSheetOpen, setPreviewSheetOpen] = useState(false);
  const closePreviewSheet = useCallback(() => setPreviewSheetOpen(false), []);
  const { hasPermission: canSeeTechnicalOptions } = useHasPermissions(
    AdminTenantPermission.Assume
  );
  // A new website starts on the first current design in picker order, the
  // recommended one, rather than a legacy design that the picker hides.
  const fallbackTemplate = useMemo(
    () =>
      sortWebsiteTemplatesForPicker(
        filterWebsiteTemplatesForPicker(templates, [])
      ).at(0) ?? templates.at(0),
    [templates]
  );
  const latestDeployment = website?.deployments?.[0] ?? null;
  const liveSiteUrl =
    website?.status === WEBSITE_STATUS.PUBLISHED && website.siteUrl
      ? website.siteUrl
      : null;

  useEffect(() => {
    if (logoDraftDirty || logoUploadActiveRef.current) {
      return;
    }

    // Background refetches return the same records; only resync the editor
    // when the saved website or the initial-form inputs actually change, so
    // unsaved edits such as a design being tried survive window focus.
    if (website) {
      const formSource = `website:${website.id}:${String(website.updatedAt)}`;
      if (syncedFormSourceRef.current === formSource) {
        return;
      }

      syncedFormSourceRef.current = formSource;
      setProvisionalSlugError(null);
      setForm({
        templateKey: website.templateKey,
        templateVersion: website.templateVersion,
        registryTemplateId: website.registryTemplateId,
        provisionalSlug: website.provisionalSlug,
        draftConfig: website.draftConfig,
      });
      return;
    }

    const initialFormSource = `initial:${company?.id ?? ''}:${
      fallbackTemplate?.registryTemplateId ?? ''
    }`;
    if (
      !templatesLoading &&
      syncedFormSourceRef.current !== initialFormSource
    ) {
      syncedFormSourceRef.current = initialFormSource;
      setForm((current) => {
        const selectedTemplate =
          templates.find(
            (template) =>
              template.registryTemplateId === current.registryTemplateId ||
              template.key === current.templateKey
          ) ?? fallbackTemplate;
        return buildInitialForm(t, company ?? undefined, selectedTemplate);
      });
    }
  }, [
    company,
    fallbackTemplate,
    logoDraftDirty,
    t,
    templates,
    templatesLoading,
    website,
  ]);

  const selectedTemplate = useMemo(
    () => resolveSelectedWebsiteTemplate(templates, form),
    [form, templates]
  );
  const logoSupported = selectedTemplate?.capabilities.logo ?? false;
  const currentLogoFileId = form.draftConfig.theme?.logoFileId;
  const currentLogoUrl = form.draftConfig.theme?.logoUrl;
  const publishedLogoIdentity =
    website?.publishedConfig?.theme?.logoFileId ??
    website?.publishedConfig?.theme?.logoUrl;
  const currentLogoIdentity = currentLogoFileId ?? currentLogoUrl;
  const showDeferredLogoCleanup = Boolean(
    publishedLogoIdentity && publishedLogoIdentity !== currentLogoIdentity
  );
  const brochureSections = form.draftConfig.content.sections;
  const neighborhoodsText = getWebsiteSectionInputText(
    brochureSections,
    'neighborhoods',
    formatStringItems
  );
  const servicesText = getWebsiteServicesInputText(
    brochureSections,
    form.draftConfig.content.services
  );
  const faqsText = getWebsiteSectionInputText(
    brochureSections,
    'faqs',
    formatFaqItems
  );
  const externalLinksText = getWebsiteSectionInputText(
    brochureSections,
    'external-links',
    formatExternalLinks
  );
  const legalText = getWebsiteSectionInputText(
    brochureSections,
    'legal',
    formatLegalText
  );

  const updateTemplate = (template: WebsiteTemplateRegistryTemplateSummary) => {
    setForm((current) => ({
      ...current,
      templateKey: template.key,
      templateVersion: template.version,
      registryTemplateId: template.registryTemplateId,
    }));
  };

  const updateMessageTemplate = (
    slot: WebsiteWhatsAppCtaSlot,
    messageTemplate: string
  ) => {
    setForm((current) => ({
      ...current,
      draftConfig: {
        ...current.draftConfig,
        whatsApp: {
          ...current.draftConfig.whatsApp,
          templates: current.draftConfig.whatsApp.templates.map((template) =>
            template.slot === slot ? { ...template, messageTemplate } : template
          ),
        },
      },
    }));
  };

  const updateThemeColor = (key: ThemeColorKey, value: string) => {
    setForm((current) => ({
      ...current,
      draftConfig: {
        ...current.draftConfig,
        theme: {
          ...current.draftConfig.theme,
          [key]: value,
        },
      },
    }));
  };

  const updateHeroImageUrl = (heroImageUrl: string) => {
    setForm((current) => ({
      ...current,
      draftConfig: {
        ...current.draftConfig,
        theme: {
          ...current.draftConfig.theme,
          heroImageUrl,
        },
      },
    }));
  };

  const updateWhatsAppPhone = (phoneNumber: string) => {
    setForm((current) => ({
      ...current,
      draftConfig: {
        ...current.draftConfig,
        whatsApp: {
          ...current.draftConfig.whatsApp,
          phoneNumber,
        },
      },
    }));
  };

  const updateProvisionalSlug = (value: string) => {
    setProvisionalSlugError(null);
    setForm((current) => ({
      ...current,
      provisionalSlug: normalizeProvisionalSlug(value),
    }));
  };

  const updateWebsiteContent = (
    content: Partial<WebsiteUpsertRequest['draftConfig']['content']>
  ) => {
    setForm((current) => ({
      ...current,
      draftConfig: {
        ...current.draftConfig,
        content: {
          ...current.draftConfig.content,
          ...content,
        },
      },
    }));
  };

  const updateBrochureSection = (
    type: BrochureSectionType,
    content: Record<string, unknown>,
    hasValue: boolean
  ) => {
    setForm((current) => ({
      ...current,
      draftConfig: {
        ...current.draftConfig,
        content: {
          ...current.draftConfig.content,
          sections: upsertBrochureSection(
            current.draftConfig.content.sections,
            {
              id: type,
              type,
              content,
            },
            hasValue
          ),
        },
      },
    }));
  };

  const updateBrochureListSection = (
    type: Extract<BrochureSectionType, 'neighborhoods' | 'services'>,
    value: string
  ) => {
    const items = parseLineList(value);
    updateBrochureSection(
      type,
      { inputText: value, items },
      Boolean(value.trim())
    );

    if (type === 'services') {
      setForm((current) => ({
        ...current,
        draftConfig: {
          ...current.draftConfig,
          content: {
            ...current.draftConfig.content,
            services: items,
          },
        },
      }));
    }
  };

  const updateFaqSection = (value: string) => {
    updateBrochureSection(
      'faqs',
      { inputText: value, items: parseFaqItems(value) },
      Boolean(value.trim())
    );
  };

  const updateExternalLinksSection = (value: string) => {
    updateBrochureSection(
      'external-links',
      { inputText: value, items: parseExternalLinks(value) },
      Boolean(value.trim())
    );
  };

  const updateLegalSection = (value: string) => {
    updateBrochureSection(
      'legal',
      { inputText: value, text: value },
      Boolean(value.trim())
    );
  };

  const isFormValid = isWebsiteSettingsFormValid(form);
  const unpublishedChanges = hasUnpublishedWebsiteChanges(form, website);
  const deploymentInProgress = isWebsiteDeploymentInProgress(website);
  const previewSiteHost = resolveWebsitePreviewHost(
    liveSiteUrl,
    form.provisionalSlug
  );
  const pendingPublishNote = deploymentInProgress
    ? t('websites.templatePreview.publishing')
    : unpublishedChanges
      ? t('websites.templatePreview.pendingPublish')
      : undefined;

  const { upsertWebsite, loading: saving } = useUpsertWebsite();
  const { createPreviewSession, loading: creatingPreview } =
    useCreateWebsitePreviewSession();
  const { publishWebsite, loading: publishing } = usePublishWebsite();
  const { createLogoUploadSession, loading: creatingLogoUploadSession } =
    useCreateWebsiteLogoUploadSession();
  const { finalizeLogoUpload, loading: finalizingLogoUpload } =
    useFinalizeWebsiteLogoUpload();

  useEffect(
    () => () => {
      logoUploadOperationRef.current += 1;
      logoUploadActiveRef.current = false;
      logoUploadAbortControllerRef.current?.abort();
      pendingLogoFileIdRef.current = null;
    },
    []
  );

  const updatePendingLogoFileId = (fileId: string | null): void => {
    pendingLogoFileIdRef.current = fileId;
    setPendingLogoFileId(fileId);
  };

  const updateLogoUploadActive = (active: boolean): void => {
    logoUploadActiveRef.current = active;
    setLogoUploadActive(active);
  };

  const reconcilePersistedLogo = (savedWebsite: WebsiteReadResponse): void => {
    const pendingFileId = pendingLogoFileIdRef.current;
    if (
      pendingFileId &&
      savedWebsite.draftConfig.theme?.logoFileId !== pendingFileId
    ) {
      return;
    }

    updatePendingLogoFileId(null);
    setLogoDraftDirty(false);
    setLogoStatusMessage(undefined);
    setLogoErrorMessage(undefined);
  };

  const logoBusy =
    saving ||
    publishing ||
    logoPublishRetryActive ||
    logoUploadActive ||
    creatingLogoUploadSession ||
    finalizingLogoUpload;
  const templateSelectionDisabled = isWebsiteTemplateSelectionDisabled(
    canUpdateWebsite,
    logoBusy,
    pendingLogoFileId
  );
  const { createDomain, loading: creatingDomain } = useCreateWebsiteDomain({
    onSuccess: () => {
      showAlert(t('websites.settings.domains.alerts.createSuccess'), 'success');
      setCustomDomain('');
    },
    onError: () =>
      showAlert(t('websites.settings.domains.alerts.createError'), 'error'),
  });
  const { verifyDomain, loading: verifyingDomain } = useVerifyWebsiteDomain({
    onSuccess: () =>
      showAlert(t('websites.settings.domains.alerts.verifySuccess'), 'success'),
    onError: () =>
      showAlert(t('websites.settings.domains.alerts.verifyError'), 'error'),
  });

  const showProvisionalSlugUnavailableError = (error: unknown): boolean => {
    if (!isProvisionalSlugUnavailableError(error)) {
      return false;
    }

    const message = t('websites.settings.alerts.provisionalSlugUnavailable');
    setProvisionalSlugError(message);
    showAlert(message, 'error');
    return true;
  };

  const getLogoErrorMessage = (error: unknown): string => {
    if (error instanceof WebsiteLogoValidationError) {
      return t(LOGO_VALIDATION_TRANSLATION_KEYS[error.code]);
    }
    if (error instanceof WebsiteLogoUploadError) {
      return t('websites.settings.logo.errors.uploadFailed');
    }

    return (
      extractApiErrorMessage(error) ||
      t('websites.settings.logo.errors.uploadFailed')
    );
  };

  const handleLogoFileSelected = async (file: File): Promise<void> => {
    if (!canStartWebsiteLogoUpload(canUpdateWebsite, logoSupported, logoBusy)) {
      return;
    }

    const operationId = logoUploadOperationRef.current + 1;
    logoUploadOperationRef.current = operationId;
    const abortController = new AbortController();
    logoUploadAbortControllerRef.current = abortController;
    const isCurrentOperation = (): boolean =>
      isCurrentWebsiteLogoUploadOperation(
        logoUploadOperationRef.current,
        operationId,
        abortController.signal
      );
    const assertCurrentOperation = (): void => {
      if (!isCurrentOperation()) {
        throw new WebsiteLogoUploadError('aborted');
      }
    };
    updateLogoUploadActive(true);
    setLogoErrorMessage(undefined);
    setLogoStatusMessage(t('websites.settings.logo.status.preparing'));
    setLogoUploadProgress(5);

    try {
      const contentType = validateWebsiteLogoFile(file);
      const checksum = await calculateWebsiteLogoChecksum(file);
      assertCurrentOperation();

      const uploadSession = await createLogoUploadSession({
        templateKey: form.templateKey,
        templateVersion: form.templateVersion,
        registryTemplateId: form.registryTemplateId,
        fileName: file.name,
        contentType,
        size: file.size,
        checksumSHA256Hex: checksum.hex,
        checksumSHA256Base64: checksum.base64,
      });
      assertCurrentOperation();

      setLogoStatusMessage(t('websites.settings.logo.status.uploading'));
      setLogoUploadProgress(10);
      await uploadWebsiteLogoBytes(
        uploadSession.uploadUrl,
        uploadSession.uploadHeaders,
        file,
        (progress) => setLogoUploadProgress(Math.max(10, progress)),
        abortController.signal
      );
      assertCurrentOperation();

      setLogoStatusMessage(t('websites.settings.logo.status.finalizing'));
      const finalized = await finalizeLogoUpload(uploadSession.uploadSessionId);
      assertCurrentOperation();

      setForm((current) => ({
        ...current,
        draftConfig: {
          ...current.draftConfig,
          theme: {
            ...current.draftConfig.theme,
            logoFileId: finalized.fileId,
            logoUrl: finalized.logoUrl,
          },
        },
      }));
      updatePendingLogoFileId(finalized.fileId);
      setLogoDraftDirty(true);
      setLogoUploadProgress(100);
      setLogoStatusMessage(t('websites.settings.logo.status.ready'));
    } catch (error) {
      if (shouldIgnoreWebsiteLogoUploadError(error, isCurrentOperation())) {
        return;
      }
      const message = getLogoErrorMessage(error);
      setLogoErrorMessage(message);
      setLogoStatusMessage(undefined);
      setLogoUploadProgress(0);
      showAlert(message, 'error');
    } finally {
      if (isCurrentOperation()) {
        logoUploadAbortControllerRef.current = null;
        updateLogoUploadActive(false);
      }
    }
  };

  const handleRemoveLogo = (): void => {
    if (!canUpdateWebsite || logoBusy || !currentLogoIdentity) return;

    if (currentLogoFileId && pendingLogoFileId === currentLogoFileId) {
      updatePendingLogoFileId(null);
    }

    setForm((current) => {
      const theme = { ...current.draftConfig.theme };
      delete theme.logoFileId;
      delete theme.logoUrl;
      return {
        ...current,
        draftConfig: {
          ...current.draftConfig,
          theme,
        },
      };
    });
    setLogoDraftDirty(true);
    setLogoErrorMessage(undefined);
    setLogoStatusMessage(t('websites.settings.logo.status.removed'));
  };

  useEffect(() => {
    const hasPendingCustomDomain = (website?.domains ?? []).some(
      (domain) =>
        domain.type === WEBSITE_DOMAIN_TYPE.CUSTOM &&
        domain.status !== WEBSITE_DOMAIN_STATUS.ACTIVE &&
        domain.status !== WEBSITE_DOMAIN_STATUS.ERROR
    );
    const hasActiveDeployment =
      website?.status === WEBSITE_STATUS.PUBLISHING ||
      latestDeployment?.status === WEBSITE_DEPLOYMENT_STATUS.QUEUED ||
      latestDeployment?.status === WEBSITE_DEPLOYMENT_STATUS.RUNNING;

    if (!hasPendingCustomDomain && !hasActiveDeployment) {
      return undefined;
    }

    const intervalId = window.setInterval(
      () => {
        void refetchWebsite();
      },
      hasActiveDeployment ? 5000 : 15000
    );

    return () => window.clearInterval(intervalId);
  }, [
    latestDeployment?.status,
    refetchWebsite,
    website?.domains,
    website?.status,
  ]);

  useEffect(() => {
    if (!trackedDeploymentId) {
      return;
    }

    const trackedDeployment = website?.deployments?.find(
      (deployment) => deployment.id === trackedDeploymentId
    );
    if (!trackedDeployment) {
      return;
    }

    if (trackedDeployment.status === WEBSITE_DEPLOYMENT_STATUS.SUCCEEDED) {
      showAlert(t('websites.settings.alerts.deploySuccess'), 'success');
      setTrackedDeploymentId(null);
      return;
    }

    if (trackedDeployment.status === WEBSITE_DEPLOYMENT_STATUS.FAILED) {
      showAlert(
        trackedDeployment.errorMessage
          ? t('websites.settings.alerts.deployFailedWithError', {
              errorMessage: trackedDeployment.errorMessage,
            })
          : t('websites.settings.alerts.deployFailed'),
        'error'
      );
      setTrackedDeploymentId(null);
    }
  }, [showAlert, t, trackedDeploymentId, website?.deployments]);

  const handleSave = async () => {
    if (!canUpdateWebsite || !isFormValid) return;

    try {
      const savedWebsite = await upsertWebsite(form);
      reconcilePersistedLogo(savedWebsite);
      setProvisionalSlugError(null);
      showAlert(t('websites.settings.alerts.saveDraftSuccess'), 'success');
    } catch (error) {
      if (showProvisionalSlugUnavailableError(error)) return;

      showAlert(t('websites.settings.alerts.saveDraftError'), 'error');
    }
  };

  const handlePreview = async () => {
    if (!canUpdateWebsite || !isFormValid) return;

    try {
      const previewSession = await createPreviewSession(form);
      const previewWindow = window.open(
        previewSession.previewUrl,
        '_blank',
        'noopener,noreferrer'
      );

      if (!previewWindow) {
        showAlert(t('websites.settings.alerts.previewBlocked'), 'warning');
      }
    } catch (error) {
      if (showProvisionalSlugUnavailableError(error)) return;

      showAlert(t('websites.settings.alerts.previewError'), 'error');
    }
  };

  const handleSaveAndDeploy = async () => {
    if (!canUpdateWebsite || !isFormValid) return;

    let draftSaved = false;
    try {
      const savedWebsite = await upsertWebsite(form);
      draftSaved = true;
      reconcilePersistedLogo(savedWebsite);
      setProvisionalSlugError(null);
      const publishedWebsite = await publishWhenWebsiteLogoIsDurable(
        () => publishWebsite({ websiteId: savedWebsite.id }),
        () => {
          setLogoPublishRetryActive(true);
          setLogoStatusMessage(
            t('websites.settings.logo.status.waitingForDurability')
          );
        }
      );
      setLogoStatusMessage(undefined);
      setTrackedDeploymentId(publishedWebsite.deployments?.[0]?.id ?? null);
      showAlert(t('websites.settings.alerts.deployStarted'), 'success');
      await refetchWebsite();
    } catch (error) {
      if (showProvisionalSlugUnavailableError(error)) return;

      if (draftSaved && isWebsiteLogoDurabilityPendingError(error)) {
        setLogoStatusMessage(
          t('websites.settings.logo.status.durabilityPending')
        );
        showAlert(
          t('websites.settings.alerts.logoDurabilityPending'),
          'warning'
        );
        return;
      }

      showAlert(t('websites.settings.alerts.deployStartError'), 'error');
    } finally {
      setLogoPublishRetryActive(false);
    }
  };

  const handleCreateDomain = async () => {
    if (!canUpdateWebsite) return;
    if (!customDomain.trim()) return;
    await createDomain({ hostname: customDomain.trim() });
  };

  if (
    templatesLoading ||
    websiteLoading ||
    companyLoading ||
    loadingReadWebsitePermission ||
    loadingUpdateWebsitePermission
  ) {
    return (
      <div className="flex flex-col h-full">
        <Panel className="h-full flex flex-col">
          <PanelHeader className="px-4 py-3 border-b">
            <div className="flex items-center gap-4">
              <PiGlobeLight className="w-8 h-8" />
              <Text as="h2" size="h2" weight="medium">
                {t('websites.settings.title')}
              </Text>
            </div>
          </PanelHeader>
          <div className="flex-1 flex items-center justify-center p-6">
            <Loading />
          </div>
        </Panel>
      </div>
    );
  }

  if (!canReadWebsite) {
    return (
      <div className="flex flex-col h-full">
        <Panel className="h-full flex flex-col">
          <PanelHeader className="px-4 py-3 border-b">
            <div className="flex items-center gap-4">
              <PiGlobeLight className="w-8 h-8" />
              <Text as="h2" size="h2" weight="medium">
                {t('websites.settings.title')}
              </Text>
            </div>
          </PanelHeader>
          <div className="flex-1 overflow-auto p-6">
            <ErrorState
              title={t('websites.settings.noAccess.title')}
              description={t('websites.settings.noAccess.description')}
            />
          </div>
        </Panel>
      </div>
    );
  }

  if (templatesError || websiteError || companyError) {
    return (
      <div className="flex flex-col h-full">
        <Panel className="h-full flex flex-col">
          <PanelHeader className="px-4 py-3 border-b">
            <div className="flex items-center gap-4">
              <PiGlobeLight className="w-8 h-8" />
              <Text as="h2" size="h2" weight="medium">
                {t('websites.settings.title')}
              </Text>
            </div>
          </PanelHeader>
          <div className="flex-1 overflow-auto p-6">
            <ErrorState
              title={t('websites.settings.error.title')}
              description={t('websites.settings.error.description')}
            />
          </div>
        </Panel>
      </div>
    );
  }

  const websiteStatus = website?.status ?? WEBSITE_STATUS.DRAFT;
  const previewDisabled =
    !canUpdateWebsite || !isFormValid || creatingPreview || logoBusy;
  // The backend rejects a second publication while one runs, so the publish
  // buttons wait for it instead of showing that error.
  const publishDisabled =
    !canUpdateWebsite ||
    !isFormValid ||
    publishing ||
    saving ||
    logoBusy ||
    deploymentInProgress;
  const publishLoading =
    saving || publishing || logoPublishRetryActive || deploymentInProgress;
  const showPreviewPanel = activeStep !== 'address';

  const stepPanels: Record<WebsiteSettingsStep, React.ReactNode> = {
    design: (
      <WebsiteDesignStep
        templates={templates}
        selectedTemplate={selectedTemplate}
        form={form}
        website={website}
        selectionDisabled={templateSelectionDisabled}
        pendingLogoFileId={pendingLogoFileId}
        showTechnicalOptions={canSeeTechnicalOptions}
        onSelectTemplate={updateTemplate}
        onTemplateKeyChange={(templateKey) =>
          setForm((current) => ({
            ...current,
            templateKey,
            registryTemplateId: undefined,
          }))
        }
        onTemplateVersionChange={(templateVersion) =>
          setForm((current) => ({
            ...current,
            templateVersion,
            registryTemplateId: undefined,
          }))
        }
      />
    ),
    brand: (
      <WebsiteBrandStep
        form={form}
        disabled={!canUpdateWebsite}
        logo={{
          logoUrl: currentLogoUrl,
          inactive: !logoSupported,
          busy: logoBusy,
          progress: logoUploadProgress,
          statusMessage: logoStatusMessage,
          errorMessage: logoErrorMessage,
          showDeferredCleanup: showDeferredLogoCleanup,
          onFileSelected: (file) => void handleLogoFileSelected(file),
          onRemove: handleRemoveLogo,
        }}
        onThemeColorChange={updateThemeColor}
        onContentChange={updateWebsiteContent}
        onHeroImageUrlChange={updateHeroImageUrl}
      />
    ),
    contact: (
      <WebsiteContactStep
        form={form}
        disabled={!canUpdateWebsite}
        analytics={analytics}
        analyticsLoading={analyticsLoading}
        analyticsHasError={Boolean(analyticsError)}
        onWhatsAppPhoneChange={updateWhatsAppPhone}
        onMessageTemplateChange={updateMessageTemplate}
        onContentChange={updateWebsiteContent}
      />
    ),
    content: (
      <WebsiteContentStep
        texts={{
          neighborhoods: neighborhoodsText,
          services: servicesText,
          faqs: faqsText,
          externalLinks: externalLinksText,
          legal: legalText,
        }}
        disabled={!canUpdateWebsite}
        onNeighborhoodsChange={(value) =>
          updateBrochureListSection('neighborhoods', value)
        }
        onServicesChange={(value) =>
          updateBrochureListSection('services', value)
        }
        onFaqsChange={updateFaqSection}
        onExternalLinksChange={updateExternalLinksSection}
        onLegalChange={updateLegalSection}
      />
    ),
    address: (
      <WebsiteAddressStep
        website={website}
        provisionalSlug={form.provisionalSlug}
        provisionalSlugError={provisionalSlugError}
        liveSiteUrl={liveSiteUrl}
        disabled={!canUpdateWebsite}
        customDomain={customDomain}
        creatingDomain={creatingDomain}
        verifyingDomain={verifyingDomain}
        onProvisionalSlugChange={updateProvisionalSlug}
        onCustomDomainChange={setCustomDomain}
        onCreateDomain={() => void handleCreateDomain()}
        onVerifyDomain={(domainId) => void verifyDomain(domainId)}
      />
    ),
  };

  const saveAction = {
    disabled: !canUpdateWebsite || !isFormValid || saving || logoBusy,
    loading: saving,
    onClick: () => void handleSave(),
  };

  return (
    <div className="flex h-full flex-col">
      <Panel className="flex h-full flex-col" variant="neutral">
        <WebsiteSettingsHeader
          status={websiteStatus}
          liveSiteUrl={liveSiteUrl}
          unpublishedChanges={unpublishedChanges}
          deploying={deploymentInProgress}
          saveAction={saveAction}
          previewAction={{
            disabled: previewDisabled,
            loading: creatingPreview,
            onClick: () => void handlePreview(),
          }}
          publishAction={{
            disabled: publishDisabled,
            loading: publishLoading,
            onClick: () => void handleSaveAndDeploy(),
          }}
        />

        <div className="flex-1 overflow-auto">
          <div className="grid max-w-[1400px] items-start gap-6 p-4 pb-28 sm:p-6 md:pb-10 lg:grid-cols-[13.5rem_minmax(0,1fr)]">
            <WebsiteSettingsSteps
              activeStep={activeStep}
              onStepChange={setActiveStep}
            />
            <div
              className={classes(
                'min-w-0',
                showPreviewPanel &&
                  'xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(340px,460px)] xl:items-start xl:gap-6'
              )}
            >
              <div className="min-w-0">{stepPanels[activeStep]}</div>
              {showPreviewPanel ? (
                <aside className="mt-6 hidden md:block xl:sticky xl:top-0 xl:mt-0">
                  <WebsiteTemplateMockPreview
                    template={selectedTemplate}
                    form={form}
                    siteHost={previewSiteHost}
                    note={pendingPublishNote}
                  />
                </aside>
              ) : null}
            </div>
          </div>
        </div>

        <WebsiteMobileActionBar
          saveDisabled={saveAction.disabled}
          saveLoading={saveAction.loading}
          publishDisabled={publishDisabled}
          publishLoading={publishLoading}
          onOpenPreview={() => setPreviewSheetOpen(true)}
          onSave={saveAction.onClick}
          onPublish={() => void handleSaveAndDeploy()}
        />
      </Panel>

      <WebsitePreviewSheet
        open={previewSheetOpen}
        template={selectedTemplate}
        form={form}
        siteHost={previewSiteHost}
        publishDisabled={publishDisabled}
        publishing={publishLoading}
        realPreviewDisabled={previewDisabled}
        onClose={closePreviewSheet}
        onPublish={() => void handleSaveAndDeploy()}
        onOpenRealPreview={() => void handlePreview()}
      />
    </div>
  );
};
